import {
  Day,
  CalendarDay,
  CalendarRoom,
  RawAvailabilityCalendar,
  Restriction,
} from './types';

interface CalendarState {
  days: Day[];
}
const state: CalendarState = {
  days: [],
};

function createEmptyDay(date: string): CalendarDay {
  return {
    date: date,
    time: new Date(date + 'T00:00:00').getTime(),
    available: true,
    availableRooms: 0,
    rooms: [],
    restrictions: [],
    closed: false,
    closeToArrival: false,
    closeToDeparture: false,
    ctaOrFullOccupancy: false,
    ctdOrFullOccupancy: false,
    maxLengthOfStay: 0,
    minLengthOfStay: 1,
    gtMaxLengthOfStay: 0,
    gtMinLengthOfStay: 0,
  };
}

function getToday(): Date {
  const dt = new Date();
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function filterRoom(room: CalendarRoom, list?: CalendarRoom[]): boolean {
  if (!list || list.length === 0) return true;
  return !!list.find((x) => x.id === room.id && x.package === room.package);
}

function getAllRoomsInDateObject(
  dateObject: any,
  roomsToFilterBy?: CalendarRoom[]
): CalendarRoom[] {
  if (!dateObject) return [];
  const rooms: CalendarRoom[] = [];

  // old endpoint structure
  if (dateObject['rooms']) {
    const roomsObject = dateObject['rooms'];
    for (const roomKey in roomsObject) {
      const r = roomsObject[roomKey];
      const room: CalendarRoom = {
        id: roomKey,
        package: r.package_id,
        available: r.avail,
        closed: r.closed === 1,
        cta: r.cta === 1,
        ctd: r.ctd === 1,
        maxlos: r.max_l,
        minlos: r.min_l,
        rate: 1,
      };

      if (room.minlos === 0) room.minlos = 1;
      if (room.rate > 0) {
        rooms.push(room);
      }
    }
  }
  // new endpoint structure
  // TODO: remove both gtmaxlos and gtminlos
  else {
    for (let i = 0; i < dateObject.length; i++) {
      const r = dateObject[i];
      const room: CalendarRoom = {
        id: r.room_type_id,
        package: r.package_id,
        available: r.avail,
        closed: r.closed === 1,
        cta: r.cta === 1,
        ctd: r.ctd === 1,
        maxlos: r.max_l,
        minlos: r.min_l,
        rate: parseFloat(r.rate),
      };

      if (room.minlos === 0) room.minlos = 1;
      if (room.rate > 0) {
        rooms.push(room);
      }
    }
  }

  return rooms.filter((x) => filterRoom(x, roomsToFilterBy));
}

function getAvailableRoomsToCheckin(
  data: RawAvailabilityCalendar,
  checkin?: string
): CalendarRoom[] {
  if (!checkin) return [];
  const rooms = getAllRoomsInDateObject(data[checkin]);
  return rooms.filter((x) => x.available && !x.cta);
}

// Process the return of endpoint /get_calendar_availability
export function processAvailabilityData(
  data: RawAvailabilityCalendar,
  checkin?: string
): CalendarDay[] {
  const checkinAvailableRooms = getAvailableRoomsToCheckin(data, checkin);

  const days: CalendarDay[] = [];

  for (const dateKey in data) {
    if (dateKey === 'xhrsCount') continue;
    const day: CalendarDay = createEmptyDay(dateKey);
    const rooms = getAllRoomsInDateObject(data[dateKey], checkinAvailableRooms);
    const openRoomsLength = rooms.filter((r) => !r.closed).length;

    const roomsLength = rooms?.length || 0;
    let ctaCount = 0;
    let ctdCount = 0;
    let closedCount = 0;
    let maxLOSCount = 0;
    let maxLOS = 0;
    let minLOSCount = 0;
    let minLOS = 99999;
    let totalAvailable = 0;
    let ctaOrFullOccupancy = true;
    let ctdOrFullOccupancy = true;
    let minlosRestrictions: Restriction[] = [];
    let maxlosRestrictions: Restriction[] = [];

    if (roomsLength > 0) {
      for (let i = 0; i < rooms.length; i++) {
        const room = rooms[i];
        day.rooms.push(room);
        if (room.cta) ctaCount++;
        if (room.ctd) ctdCount++;
        if (room.closed) closedCount++;

        if (!room.closed) {
          if (room.minlos >= 1 && room.minlos < 200) {
            minLOSCount++;
            minLOS = Math.min(room.minlos, minLOS);
            minlosRestrictions.push({
              room_id: room.id,
              value: room.minlos,
              type: 'minlos',
            });
          }

          if (room.maxlos > 0 && room.maxlos < 200) {
            maxLOSCount++;
            maxLOS = Math.max(room.maxlos, maxLOS);
            maxlosRestrictions.push({
              room_id: room.id,
              value: room.maxlos,
              type: 'maxlos',
            });
          }
        }

        totalAvailable += room.available > 0 ? 1 : 0;

        if (room.available > 0 && !room.cta) ctaOrFullOccupancy = false;
        if (!room.ctd) ctdOrFullOccupancy = false;
      }

      minlosRestrictions =
        minLOS <= 1 ? [] : minlosRestrictions.filter((x) => x.value === minLOS);
      maxlosRestrictions =
        maxLOS === 0
          ? []
          : maxlosRestrictions.filter((x) => x.value === maxLOS);

      day.closeToArrival = ctaCount === openRoomsLength;
      day.closeToDeparture = ctdCount > 0 && ctdCount === openRoomsLength;
      day.closed = closedCount === roomsLength;
      day.available = true;
      day.availableRooms = totalAvailable;
      day.ctaOrFullOccupancy = ctaOrFullOccupancy;
      day.ctdOrFullOccupancy = ctdOrFullOccupancy;

      if (maxLOSCount === openRoomsLength) {
        day.maxLengthOfStay = maxLOS;
        day.restrictions.push(...maxlosRestrictions);
      }

      if (minLOSCount === openRoomsLength) {
        day.minLengthOfStay = minLOS;
        day.restrictions.push(...minlosRestrictions);
      }
    }

    days.push(day);
  }

  return days;
}

function applyRestrictions(
  day: Day,
  checkin?: Day,
  noAvailableDateFound?: boolean,
  foundAClosedDayInCheckOutRange?: boolean
) {
  if (checkin && day.date === checkin.date) {
    return;
  }

  if (!checkin && day.availableRooms === 0) {
    day.available = false;
    day.blockReasons.push('no available rooms');
  }

  if (checkin && noAvailableDateFound) {
    day.available = false;
    day.blockReasons.push('no available date inside range');
  }

  // disable closed rooms
  if (!checkin && day.closed) {
    day.available = false;
    day.closed = true;
    day.blockReasons.push('closed');
  }

  // CTA
  if (!checkin && day.closeToArrival) {
    day.available = false;
    day.blockReasons.push('cta');
  }

  // 100% are CTA or Occupancy
  if (!checkin && day.ctaOrFullOccupancy) {
    day.available = false;
    day.blockReasons.push('cta or occupancy');
  }

  // 100% are CTD or Occupancy
  if (checkin && day.ctdOrFullOccupancy) {
    if (
      day.previousDay &&
      day.previousDay &&
      day.previousDay.ctdOrFullOccupancy
    ) {
      day.available = false;
      day.blockReasons.push('ctd or occupancy');
    }
  }

  // CTD
  if (checkin && day.closeToDeparture) {
    day.available = false;
    day.blockReasons.push('ctd');
  }

  // Occupancy for checkin
  if (!checkin && !day.available) {
    day.available = false;
    day.blockReasons.push('occupancy for checkin');
  }

  if (checkin && foundAClosedDayInCheckOutRange) {
    day.available = false;
    day.blockReasons.push('found a closed day in checkout range');
  }

  // MIN AND MAX LENGTH OF STAY
  if (checkin) {
    const minLOS = checkin.minLengthOfStay < 1 ? 1 : checkin.minLengthOfStay;
    const maxLOS = checkin.maxLengthOfStay;
    const diffInDays: number =
      (day.time - checkin.time) / (24 * 60 * 60 * 1000);
    if (diffInDays > 0) {
      if (minLOS > 1 && diffInDays < minLOS) {
        day.available = false;
        day.blockReasons.push('date lower than minlos');
      }
      if (maxLOS > 0 && diffInDays > maxLOS) {
        day.available = false;
        day.blockReasons.push('date greater than maxlos');
      }
    }
  }
}

export function getDay(date: string): Day | undefined {
  return state.days.find((d) => d && d.date === date);
}

function isRoomOnList(room: CalendarRoom, list: CalendarRoom[]): boolean {
  for (let i = 0; i < list.length; i++) {
    if (list[i].id === room.id && list[i].package === room.package) return true;
  }
  return false;
}

export function calculate(data: RawAvailabilityCalendar, checkin?: string) {
  const todayTime = getToday().getTime();
  const checkinTime = checkin ? new Date(`${checkin}T00:00:00`).getTime() : 0;

  const list: Day[] = [];

  const calendarDays: CalendarDay[] = processAvailabilityData(
    data,
    checkin
  ).sort((a, b) => a.time - b.time);
  const calendarDaysSize: number = calendarDays.length;

  let checkinDay: Day | undefined = undefined;
  let previousDay: Day | undefined = undefined;

  for (let i = 0; i < calendarDaysSize; i++) {
    const calendarDay = calendarDays[i];
    const day: Day = new Day(calendarDay);

    const d: Date = new Date(`${day.date}T00:00:00`);
    day.val = d.getDate();
    day.time = d.getTime();

    if (checkinDay) {
      const availableRoomsOnCheckin = checkinDay.rooms.filter(
        (x) => x.available
      );

      // remove all rooms that are not available on checkin
      day.rooms = day.rooms.filter((x) =>
        isRoomOnList(x, availableRoomsOnCheckin)
      );
    }

    if (day.time === todayTime) {
      day.today = true;
    } else if (day.time < todayTime) {
      day.past = true;
    }

    if (day.time < checkinTime) {
      day.available = false;
    }

    if (day.date === checkin) {
      checkinDay = day;
    }

    day.previousDay = previousDay
      ? { ...(previousDay as Day), previousDay: undefined }
      : undefined;
    previousDay = day;

    list.push(day);
  }

  let noAvailableDateFound = false;
  let foundAClosedDayInCheckOutRange = false;
  for (let i = 0; i < list.length; i++) {
    applyRestrictions(
      list[i],
      checkinDay,
      noAvailableDateFound,
      foundAClosedDayInCheckOutRange
    );
    if (checkinTime > 0 && list[i].time > checkinTime && list[i].closed)
      foundAClosedDayInCheckOutRange = true;
    const day: Day = list[i];
    if (
      checkinDay &&
      !noAvailableDateFound &&
      day.time >=
        checkinTime + checkinDay.minLengthOfStay * (24 * 60 * 60 * 1000) &&
      day.availableRooms === 0
    ) {
      noAvailableDateFound = true;
    }
  }

  state.days = list;
  return state.days;
}
